import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { useNotificationContext } from 'core/states';
import { cacheService } from 'dashboard/services';
import { ICacheEntry } from 'dashboard/types';

export const useGetCaches = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;

  const { data, isError, isLoading, refetch } = useQuery(
    ['adminCaches'],
    ({ signal }) => cacheService.getCaches(signal),
    { enabled }
  );

  return {
    caches: (data?.caches ?? []) as ICacheEntry[],
    environment: data?.environment ?? '',
    isError,
    isLoading,
    refetchCaches: () => refetch({ cancelRefetch: true }),
  };
};

export const useClearCache = () => {
  const { t } = useTranslation();

  const { showSuccessNotification, showErrorNotification } = useNotificationContext();

  const { errors } = useHandleErrors({
    loading: { message: t('messageClearCacheInProgress') },
    error: { title: t('errorTitleClearCache') },
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(cacheService.clearCache, {
    ...errors,
    // Replaces the generic handler rather than composing with it: useHandleErrors
    // branches on the response's own `success` flag and would raise a second,
    // generic toast over the specific one below.
    onSuccess: (result) => {
      if (!result?.success) {
        showErrorNotification(
          { title: t('errorTitleClearCache'), message: t('messageClearCacheRejected') },
          NotificationPosition.TOP
        );

        return;
      }

      // Only some mechanisms can say how much went — Redis deleteAll() returns
      // nothing — so the count is reported when it is real and omitted when it
      // would be invented.
      showSuccessNotification(
        {
          message:
            null === result.cleared
              ? t('messageClearCacheSuccess')
              : t('messageClearCacheSuccessCounted', { count: result.cleared }),
        },
        NotificationPosition.TOP
      );
    },
  });

  return { clearCache: mutateAsync, isClearCacheLoading: isLoading };
};
